import React from 'react';
import ReactButton from '@material-ui/core/Button';
import { makeStyles } from '../../theme';
import IconButton from '@material-ui/core/IconButton';
import * as Icons from '../icons';
import cx from 'classnames';
import Progress from '../progress';
import Box, { HORIZONTAL } from '../layout/box';
import * as componentStyling from '../styling';

/**
 * Use `theme settings` as much as possible instead of hard-coded values.
 * More theme settings on: https://material-ui.com/customization/theming/
 */
const commonStyles = makeStyles(
	({ shape, spacing }) => ({
		button: {
			borderRadius: shape.borderRadius * 4,
			textTransform: 'none',
			maxWidth: 200,
			margin: spacing(2.5, 0.625, 2.5, 0)
		}
	})
);

/**
 * Use `theme settings` as much as possible instead of hard-coded values.
 * More theme settings on: https://material-ui.com/customization/theming/
 */
const normalBtnStyles = makeStyles(
	({ palette }) => ({
		normalBtn: {
			backgroundColor: palette.secondary.main,
			color: palette.secondary.contrastText,
			border: '2px solid transparent',
			boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.16)',
			flexGrow: 1,

			'&:hover': {
				backgroundColor: palette.secondary.light,
				borderColor: palette.secondary.dark
			},

			'&:focus': {
				borderColor: palette.action.focus
			}
		},
		iconBtn: {
			padding:0
		}
	})
);

const Button = React.memo(
	({ label, icon, variant = 'contained', className, form, ...props}) => {
		const commonClasses = commonStyles();
		const classes = normalBtnStyles();

		let buttonInst;

		if(icon && Icons[icon]) {
			const IconCls = Icons[icon];
			buttonInst = (
				<IconButton 
				{...props}
				className={cx(classes.iconBtn, className, componentStyling.Control)}
				>
					<IconCls/>
				</IconButton>
			);
		} else {
			buttonInst = (
				<ReactButton 
					{...props}
					variant={variant}
					className={cx(commonClasses.button, classes.normalBtn, className, componentStyling.Control)}
					>
					{label}
				</ReactButton>
			);
		}

		if(form){
			return <Form {...form}>{buttonInst}</Form>
		}
		return buttonInst;
	}
);

export default Button;

function Form({children, action, method='post', fields = {}}) {
	const formRef = React.useRef();
	if(!action) return;
	const handleClick = () => {
		if(formRef.current)formRef.current.submit();
	}

	return (
		<form ref={formRef} action={action.replace(/&amp;/g, "&")} method={method}>
			{Object.keys(fields).map((field, i) => (<input 
					type="hidden"
					key={`hidden-${i}`}
					id={field}
					name={field}
					value={fields[field]}
					/>))}
			{React.cloneElement(React.Children.only(children), {onClick:handleClick})}
		</form>
	);
}


/**
 * Use `theme settings` as much as possible instead of hard-coded values.
 * More theme settings on: https://material-ui.com/customization/theming/
 */
const importantBtnStyles = makeStyles(
	({ palette }) => ({
		importantBtn: {
			backgroundColor: palette.primary.main,
			color: palette.primary.contrastText,
			border: `2px solid ${palette.primary.dark}`,
			flexGrow: 1,

			'&:hover': {
				backgroundColor: palette.primary.light
			},

			'&:disabled': {
				backgroundColor: palette.action.disabled,
				borderColor: 'transparent'
			},

			'&:focus': {
				backgroundColor: palette.primary.light,
				borderColor: palette.action.focus
			}
		},
		importantBox: {
			alignItems: 'center'
		}
	})
);

export const ImportantButton = React.memo(
	({ label, className, suspense, clickOnMount, ...props}) => {
		const commonClasses = commonStyles();
		const classes = importantBtnStyles();

		if(clickOnMount && props.onClick) {
			props.onClick();
			return null;
		}

		const button = (
			<ReactButton 
				{...props}
				className={cx(commonClasses.button, classes.importantBtn, className, componentStyling.Control)}
				>
				{label}
			</ReactButton>
		);

		return suspense ? (
			<Box type={HORIZONTAL} className={cx(classes.importantBox, componentStyling.Control)}>
				{ button }
				<Progress/>
			</Box>
		) : button;
	}
);


/**
 * Use `theme settings` as much as possible instead of hard-coded values.
 * More theme settings on: https://material-ui.com/customization/theming/
 */
const actionBtnStyles = makeStyles(
	({ palette }) => ({
		actionBtn: {
			backgroundColor: palette.secondary.main,
			color: palette.secondary.contrastText,
			border: `1px solid ${palette.secondary.dark}`,
			margin: 0,

			'&:hover': {
				backgroundColor: palette.secondary.light,
				boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.7)'
			},

			'&:focus': {
				backgroundColor: palette.secondary.light,
				borderColor: palette.action.focus,
				boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.7)',
			},
		}
	})
);

export const ActionButton = React.memo(
	({ label, className, ...props}) => {
		const commonClasses = commonStyles();
		const classes = actionBtnStyles();

		return (
			<ReactButton 
				{...props}
				className={cx(commonClasses.button, classes.actionBtn, className)}
				>
				{label}
			</ReactButton>
		);
	}
);


/**
 * Use `theme settings` as much as possible instead of hard-coded values.
 * More theme settings on: https://material-ui.com/customization/theming/
 */
const btnRowStyles = makeStyles({
	row: {
		alignItems: 'center'
	}
});

export const ButtonRow = React.memo(
	({ children }) => {
		const classes = btnRowStyles();
		return(
			<Box type={HORIZONTAL} className={cx(classes.row, 'ButtonRow')} >
				{children}	
			</Box>
		);
	}
);